import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { MessageSquare, Send, Image as ImageIcon, RefreshCw, Phone, Loader2, ArrowLeft, Volume2, VolumeX, Mic, Square, Zap, X, Maximize2, Minimize2, Circle, User as UserIcon, ArchiveRestore, Archive } from 'lucide-react';
import { toast } from 'sonner';
import { listChatThreads, listChatMessages, sendChatMessage, uploadMedia, getQuickReplies, saveQuickReplies, closeChatThread, reopenChatThread, getChatUnreadSummary } from '../../../lib/api';
import { resolveMediaUrl } from '../MediaUpload';

/* ─────────────────────────────  helpers  ────────────────────────────── */
const fmtTime = (iso) => { try { return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); } catch { return ''; } };
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(); } catch { return ''; } };

function playBeep() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(1320, ctx.currentTime + 0.08);
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.35, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.32);
    osc.connect(gain).connect(ctx.destination);
    osc.start(); osc.stop(ctx.currentTime + 0.34);
    setTimeout(() => ctx.close(), 500);
  } catch { /* */ }
}

/**
 * Lock the page so ONLY the chat content scrolls.
 * Adds `overflow:hidden` to <html> and <body> while the chat is mounted,
 * stores previous values and restores them on unmount.
 */
function usePageScrollLock() {
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prev = {
      htmlOverflow: html.style.overflow,
      bodyOverflow: body.style.overflow,
      bodyTouch: body.style.touchAction,
    };
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    body.style.touchAction = 'none';
    return () => {
      html.style.overflow = prev.htmlOverflow;
      body.style.overflow = prev.bodyOverflow;
      body.style.touchAction = prev.bodyTouch;
    };
  }, []);
}

/* ─────────────────────────────  Section  ────────────────────────────── */
export default function ChatSection() {
  usePageScrollLock();

  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [loadingT, setLoadingT] = useState(true);
  const [loadingM, setLoadingM] = useState(false);
  const [sending, setSending] = useState(false);
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem('chat_sound') !== 'off');
  const [showClosed, setShowClosed] = useState(false);
  const [fullscreen, setFullscreen] = useState(() => localStorage.getItem('chat_fullscreen') === 'on');
  const [q, setQ] = useState(''); // search filter for threads

  const fileRef = useRef(null);
  const scrollRef = useRef(null);
  const lastIdRef = useRef(null);
  const stickToBottomRef = useRef(true);
  // Track per-thread last-seen last_msg_at so we can beep on ANY new msg —
  // even for threads that aren't currently open.
  const lastSeenRef = useRef({});

  /* ── data loaders ────────────────────────────────────────────────── */
  const reloadThreads = useCallback(async (silent = false) => {
    if (!silent) setLoadingT(true);
    try {
      const list = await listChatThreads({ includeClosed: showClosed });
      // Detect a new incoming message on any thread (compared to last poll)
      let sawNew = false;
      const nextSeen = { ...lastSeenRef.current };
      list.forEach((t) => {
        const key = t.id;
        const prev = nextSeen[key];
        const now = t.last_msg_at || '';
        if (prev !== undefined && now && now !== prev && t.unread_count > 0) sawNew = true;
        nextSeen[key] = now;
      });
      lastSeenRef.current = nextSeen;
      if (sawNew && soundOn) playBeep();
      setThreads(list);
    } catch { /* */ }
    finally { setLoadingT(false); }
  }, [showClosed, soundOn]);

  const loadMessages = useCallback(async (t, silent = false) => {
    if (!t) return;
    if (!silent) setLoadingM(true);
    try {
      const list = await listChatMessages(t.id);
      const lastId = list.length ? list[list.length - 1].id : null;
      // Beep on a NEW incoming user message when the active thread receives it
      if (lastIdRef.current && lastId && lastId !== lastIdRef.current) {
        const last = list[list.length - 1];
        if (last && last.role === 'user' && soundOn) playBeep();
      }
      lastIdRef.current = lastId;
      setMessages((prev) => {
        if (prev.length === list.length && (prev[prev.length - 1]?.id === lastId)) return prev;
        return list;
      });
      setActive((cur) => (cur && cur.id === t.id ? cur : t));
    } catch { if (!silent) toast.error('Failed to load chat'); }
    finally { if (!silent) setLoadingM(false); }
  }, [soundOn]);

  /* ── lifecycle: initial load + FAST polling ──────────────────────── */
  useEffect(() => { reloadThreads(); }, [reloadThreads]);

  useEffect(() => {
    if (!active) return;
    // 1.2s polling for the OPEN thread — feels near-realtime without hammering
    const t = setInterval(() => { loadMessages(active, true); }, 1200);
    return () => clearInterval(t);
  }, [active?.id, loadMessages]);

  useEffect(() => {
    // 2s polling for the thread list (unread counts + new threads)
    const t = setInterval(() => reloadThreads(true), 2000);
    return () => clearInterval(t);
  }, [reloadThreads]);

  useEffect(() => {
    if (!scrollRef.current || !stickToBottomRef.current) return;
    scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'auto' });
  }, [messages]);

  useEffect(() => { localStorage.setItem('chat_fullscreen', fullscreen ? 'on' : 'off'); }, [fullscreen]);

  const onScroll = (e) => {
    const el = e.target;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
    stickToBottomRef.current = atBottom;
  };

  /* ── derived: totals + filtered thread list ──────────────────────── */
  const totalUnread = useMemo(() => threads.reduce((s, t) => s + (t.unread_count || 0), 0), [threads]);
  const filteredThreads = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return threads;
    return threads.filter((t) => (
      (t.user_name || '').toLowerCase().includes(needle) ||
      (t.user_mobile || '').includes(needle) ||
      (t.tg_username || '').toLowerCase().includes(needle) ||
      (t.last_text || '').toLowerCase().includes(needle)
    ));
  }, [q, threads]);

  /* ── actions ─────────────────────────────────────────────────────── */
  const toggleSound = () => {
    setSoundOn((s) => { localStorage.setItem('chat_sound', s ? 'off' : 'on'); return !s; });
  };
  const toggleFullscreen = () => setFullscreen((f) => !f);

  const send = async (kind = 'text', media_url = '') => {
    if (!active) return;
    if (kind === 'text' && !text.trim()) return;
    setSending(true);
    // Optimistic bubble so the admin sees their message instantly
    const optimisticId = `o-${Date.now()}`;
    const optimistic = { id: optimisticId, role: 'admin', kind, text: kind === 'text' ? text.trim() : '', media_url, created_at: new Date().toISOString(), _pending: true };
    setMessages((prev) => [...prev, optimistic]);
    stickToBottomRef.current = true;
    const payloadText = text.trim();
    setText('');
    try {
      await sendChatMessage({ thread_id: active.id, text: payloadText, kind, media_url });
      // Refresh from server to get canonical ordering + IDs
      await loadMessages(active, true);
    } catch (e) {
      setMessages((prev) => prev.filter((m) => m.id !== optimisticId));
      toast.error(e?.response?.data?.detail || 'Failed to send');
      setText(payloadText); // restore draft on failure
    } finally { setSending(false); }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    try {
      const res = await uploadMedia(f);
      const kind = f.type.startsWith('video/') ? 'video'
        : f.type.startsWith('audio/') ? 'audio'
        : 'image';
      await send(kind, res.url);
    } catch { toast.error('Upload failed'); }
  };

  const sendBlob = async (blob, kind) => {
    try {
      const ext = blob.type.includes('webm') ? 'webm'
        : blob.type.includes('mp4') ? 'mp4'
        : 'ogg';
      const file = new File([blob], `voice-${Date.now()}.${ext}`, { type: blob.type });
      const res = await uploadMedia(file);
      await send(kind, res.url);
    } catch { toast.error('Upload failed'); }
  };

  const closeThread = async (thread) => {
    if (!thread) return;
    if (!window.confirm(`Close chat with ${thread.user_name || 'this user'}?`)) return;
    try {
      await closeChatThread(thread.id);
      toast.success('Chat closed');
      if (active?.id === thread.id) setActive(null);
      await reloadThreads(true);
    } catch { toast.error('Failed to close'); }
  };

  const reopenThread = async (thread) => {
    try {
      await reopenChatThread(thread.id);
      toast.success('Chat reopened');
      await reloadThreads(true);
    } catch { toast.error('Failed to reopen'); }
  };

  /* ────────────────────────────  render  ──────────────────────────── */
  return (
    <div
      className={`fixed inset-y-0 right-0 bg-zinc-950 flex flex-col z-30 ${fullscreen ? 'left-0 top-0' : 'left-0 lg:left-72 top-[57px]'}`}
      data-testid="chat-section"
    >
      {/* Toolbar */}
      <div className="shrink-0 flex items-center justify-between px-3 md:px-5 py-2.5 border-b border-zinc-800 bg-zinc-900/70 backdrop-blur">
        <div className="flex items-center gap-2 min-w-0">
          <MessageSquare size={16} className="text-[color:var(--gold)] shrink-0" />
          <span className="text-zinc-100 font-bold text-sm truncate">Live Chat</span>
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" title="Live" />
          {totalUnread > 0 && (
            <span className="ml-1 inline-flex items-center h-5 min-w-[20px] px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold" data-testid="chat-total-unread">
              {totalUnread > 99 ? '99+' : totalUnread}
            </span>
          )}
          <span className="hidden md:inline text-zinc-500 text-xs ml-2">{threads.length} conversation{threads.length === 1 ? '' : 's'}</span>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            onClick={() => setShowClosed((v) => !v)}
            className={`h-8 px-2.5 rounded-md border text-xs flex items-center gap-1 transition ${showClosed ? 'bg-amber-500/15 text-amber-300 border-amber-700/40' : 'bg-zinc-900 text-zinc-400 border-zinc-800 hover:text-zinc-200'}`}
            data-testid="chat-toggle-closed"
            title={showClosed ? 'Hide closed chats' : 'Show closed chats'}
          >
            <Archive size={13} /> {showClosed ? 'All' : 'Open'}
          </button>
          <button
            onClick={toggleSound}
            className={`h-8 w-8 rounded-md border flex items-center justify-center transition ${soundOn ? 'bg-emerald-500/15 text-emerald-300 border-emerald-700/40' : 'bg-zinc-900 text-zinc-400 border-zinc-800'}`}
            data-testid="chat-sound-toggle"
            title={soundOn ? 'Sound on' : 'Sound off'}
          >
            {soundOn ? <Volume2 size={14} /> : <VolumeX size={14} />}
          </button>
          <button
            onClick={toggleFullscreen}
            className="h-8 w-8 rounded-md bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-zinc-300 flex items-center justify-center"
            data-testid="chat-fullscreen-toggle"
            title={fullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          >
            {fullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
          </button>
          <button
            onClick={() => reloadThreads()}
            className="h-8 w-8 rounded-md bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-zinc-300 flex items-center justify-center"
            data-testid="chat-refresh-btn"
            title="Refresh"
          >
            <RefreshCw size={12} />
          </button>
        </div>
      </div>

      {/* Body: threads + messages */}
      <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-[320px_1fr] lg:grid-cols-[360px_1fr]">
        {/* Threads list */}
        <aside
          className={`border-r border-zinc-800 bg-zinc-950 flex flex-col min-h-0 ${active ? 'hidden md:flex' : 'flex'}`}
          data-testid="chat-threads-list"
        >
          {/* Search */}
          <div className="shrink-0 p-2 border-b border-zinc-800">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search chats…"
              className="w-full h-9 px-3 rounded-md bg-zinc-900 border border-zinc-800 text-zinc-100 text-sm placeholder:text-zinc-500"
              data-testid="chat-search-input"
            />
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
            {loadingT && threads.length === 0 ? (
              <div className="p-6 text-zinc-500 text-sm flex items-center gap-2">
                <Loader2 size={14} className="animate-spin" /> Loading threads…
              </div>
            ) : filteredThreads.length === 0 ? (
              <div className="p-6 text-zinc-500 text-sm text-center">
                {threads.length === 0 ? 'No chats yet. When users DM your bot they appear here.' : 'No chats match your search.'}
              </div>
            ) : filteredThreads.map((t) => {
              const initials = (t.user_name || 'G').replace(/[^a-zA-Z0-9]/g, '').slice(0, 2).toUpperCase() || 'G';
              const isActive = active?.id === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => { stickToBottomRef.current = true; loadMessages(t); }}
                  className={`w-full text-left px-3 py-3 border-b border-zinc-900/60 hover:bg-zinc-800/50 transition flex items-start gap-3 ${isActive ? 'bg-zinc-800/70 border-l-2 border-l-[color:var(--gold)]' : 'border-l-2 border-l-transparent'}`}
                  data-testid={`chat-thread-${t.id}`}
                >
                  {/* Avatar */}
                  <div className="relative shrink-0">
                    <div className={`w-11 h-11 rounded-full flex items-center justify-center font-bold text-sm shadow-inner ${t.is_guest ? 'bg-zinc-800 text-zinc-400 border border-zinc-700' : 'bg-gradient-to-br from-[color:var(--gold)]/40 to-[color:var(--gold)]/10 text-[color:var(--gold)] border border-[color:var(--gold)]/30'}`}>
                      {initials || <UserIcon size={16} />}
                    </div>
                    {t.unread_count > 0 && (
                      <span className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold border-2 border-zinc-950">
                        {t.unread_count > 9 ? '9+' : t.unread_count}
                      </span>
                    )}
                  </div>
                  {/* Body */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-1">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span className={`truncate text-sm ${t.unread_count > 0 ? 'font-bold text-zinc-100' : 'text-zinc-200'}`}>{t.user_name || 'Guest'}</span>
                        {t.is_guest && (
                          <span className="text-[9px] uppercase tracking-widest px-1 py-0.5 rounded bg-zinc-800 text-zinc-400 border border-zinc-700 shrink-0">Guest</span>
                        )}
                        {t.closed && (
                          <span className="text-[9px] uppercase tracking-widest px-1 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-700/40 shrink-0">Closed</span>
                        )}
                      </div>
                      <span className="shrink-0 text-[10px] text-zinc-500">{fmtDate(t.last_msg_at)}</span>
                    </div>
                    {(t.user_mobile || t.tg_username) && (
                      <div className="text-zinc-500 text-[11px] truncate mt-0.5 flex items-center gap-1">
                        {t.user_mobile ? <><Phone size={9} /> +{t.user_mobile}</> : null}
                        {t.tg_username ? <span className="text-zinc-600">{t.user_mobile ? ' · ' : ''}@{t.tg_username}</span> : null}
                      </div>
                    )}
                    <div className={`text-xs mt-1 truncate ${t.unread_count > 0 ? 'text-zinc-100 font-semibold' : 'text-zinc-500'}`}>
                      {t.last_text || <span className="italic opacity-70">no messages yet</span>}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </aside>

        {/* Messages pane */}
        <section
          className={`flex flex-col min-w-0 min-h-0 bg-[#0b141a] ${active ? 'flex' : 'hidden md:flex'}`}
          data-testid="chat-pane"
        >
          {!active ? (
            <div className="flex-1 flex flex-col items-center justify-center text-zinc-600 text-sm gap-2">
              <MessageSquare size={32} className="opacity-40" />
              <span>Select a chat to start</span>
            </div>
          ) : (
            <>
              {/* Conversation header */}
              <div className="shrink-0 px-3 md:px-4 py-2.5 bg-[#202c33] border-b border-zinc-800 flex items-center gap-2">
                <button
                  onClick={() => setActive(null)}
                  className="md:hidden text-zinc-300 hover:text-white p-1"
                  aria-label="Back"
                  data-testid="chat-back-btn"
                >
                  <ArrowLeft size={20} />
                </button>
                <div className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm shrink-0 ${active.is_guest ? 'bg-zinc-800 text-zinc-400 border border-zinc-700' : 'bg-[color:var(--gold)]/20 border border-[color:var(--gold)]/40 text-[color:var(--gold)]'}`}>
                  {(active.user_name || '?').slice(0, 1).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="font-bold text-zinc-100 truncate text-sm">{active.user_name || 'Guest'}</span>
                    {active.is_guest && <span className="text-[9px] uppercase tracking-widest px-1 py-0.5 rounded bg-zinc-800 text-zinc-400 border border-zinc-700">Guest</span>}
                    {active.closed && <span className="text-[9px] uppercase tracking-widest px-1 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-700/40">Closed</span>}
                  </div>
                  <div className="text-zinc-400 text-[11px] truncate">
                    {active.user_mobile ? `+${active.user_mobile}` : ''}
                    {active.tg_username ? `${active.user_mobile ? ' · ' : ''}@${active.tg_username}` : ''}
                  </div>
                </div>
                {/* Close / Reopen thread */}
                {active.closed ? (
                  <button
                    onClick={() => reopenThread(active)}
                    className="h-8 px-2.5 rounded-md bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 border border-emerald-700/40 text-xs flex items-center gap-1"
                    data-testid="chat-reopen-btn"
                    title="Reopen chat"
                  >
                    <ArchiveRestore size={13} /> Reopen
                  </button>
                ) : (
                  <button
                    onClick={() => closeThread(active)}
                    className="h-8 px-2.5 rounded-md bg-zinc-900 hover:bg-red-500/15 hover:text-red-300 text-zinc-300 border border-zinc-800 text-xs flex items-center gap-1"
                    data-testid="chat-close-thread-btn"
                    title="Close chat"
                  >
                    <X size={13} /> Close
                  </button>
                )}
              </div>

              {/* Scrollable message list (the ONLY scrollable thing in the chat) */}
              <div
                ref={scrollRef}
                onScroll={onScroll}
                className="flex-1 min-h-0 overflow-y-auto overscroll-contain chat-scroll p-2 md:p-4 space-y-2"
                style={{
                  background: '#0b141a',
                  backgroundImage: 'radial-gradient(rgba(255,255,255,0.025) 1px, transparent 1px)',
                  backgroundSize: '16px 16px',
                }}
                data-testid="chat-messages"
              >
                {loadingM && messages.length === 0 ? (
                  <div className="text-zinc-500 text-sm flex items-center gap-2">
                    <Loader2 size={14} className="animate-spin" /> Loading…
                  </div>
                ) : messages.map((m) => {
                  const isAdmin = m.role === 'admin';
                  const isBot = m.role === 'bot';
                  const mediaUrl = resolveMediaUrl(m.media_url);
                  return (
                    <div key={m.id} className={`flex ${isAdmin || isBot ? 'justify-end' : 'justify-start'}`}>
                      <div
                        className={`max-w-[85%] sm:max-w-[75%] px-2.5 py-1.5 rounded-lg shadow-sm ${isAdmin ? 'bg-[#005c4b] text-white' : isBot ? 'bg-[#3b3b5c] text-zinc-100' : 'bg-[#202c33] text-zinc-100'} ${m._pending ? 'opacity-70' : ''}`}
                        data-testid={`chat-msg-${m.id}`}
                      >
                        {isBot && (
                          <div className="text-[10px] uppercase tracking-wider text-purple-300 mb-0.5">AI Bot</div>
                        )}
                        {m.kind === 'image' && mediaUrl && (
                          <a href={mediaUrl} target="_blank" rel="noreferrer">
                            <img
                              src={mediaUrl}
                              alt=""
                              onLoad={() => {
                                if (stickToBottomRef.current && scrollRef.current) {
                                  scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
                                }
                              }}
                              className="max-w-[280px] max-h-[300px] rounded mb-1 object-cover"
                            />
                          </a>
                        )}
                        {m.kind === 'video' && mediaUrl && (
                          <video
                            src={mediaUrl}
                            controls
                            onLoadedData={() => {
                              if (stickToBottomRef.current && scrollRef.current) {
                                scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
                              }
                            }}
                            className="max-w-[280px] rounded mb-1"
                          />
                        )}
                        {m.kind === 'audio' && mediaUrl && (
                          <audio src={mediaUrl} controls className="mb-1 max-w-full" />
                        )}
                        {m.kind === 'file' && mediaUrl && (
                          <a href={mediaUrl} download className="text-[color:var(--gold)] underline text-sm break-all">
                            Download file
                          </a>
                        )}
                        {m.text && <div className="text-sm whitespace-pre-wrap break-words">{m.text}</div>}
                        <div className="text-[10px] text-white/60 text-right mt-0.5">{fmtTime(m.created_at)}</div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Composer (disabled for closed threads) */}
              {active.closed ? (
                <div className="shrink-0 px-4 py-3 bg-[#202c33] border-t border-zinc-800 text-center text-amber-300/90 text-sm">
                  This chat is closed. Click <span className="font-bold">Reopen</span> above to continue the conversation.
                </div>
              ) : (
              <div className="shrink-0 px-2 md:px-3 py-2 bg-[#202c33] border-t border-zinc-800 flex items-center gap-2">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*,video/*,audio/*"
                  onChange={onFile}
                  className="hidden"
                  data-testid="chat-file-input"
                />
                <button
                  onClick={() => fileRef.current?.click()}
                  className="h-10 w-10 rounded-full bg-zinc-800 hover:bg-zinc-700 text-zinc-300 flex items-center justify-center shrink-0"
                  data-testid="chat-attach-btn"
                  title="Attach"
                >
                  <ImageIcon size={18} />
                </button>
                <VoiceRecordButton onSend={(blob) => sendBlob(blob, 'audio')} />
                <QuickReplyMenu onPick={(t) => setText(t)} />
                <input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && send('text')}
                  placeholder="Type a message…"
                  className="flex-1 h-10 px-3 bg-zinc-900 border border-zinc-800 rounded-full text-sm text-zinc-100 min-w-0"
                  data-testid="chat-input"
                />
                <button
                  onClick={() => send('text')}
                  disabled={sending}
                  className="h-10 w-10 rounded-full bg-[color:var(--gold)] hover:brightness-110 text-black flex items-center justify-center disabled:opacity-60 shrink-0"
                  data-testid="chat-send-btn"
                >
                  <Send size={18} />
                </button>
              </div>
              )}
            </>
          )}
        </section>
      </div>
    </div>
  );
}

/* ─────────────────────────  Quick replies  ──────────────────────────── */
const DEFAULT_QUICK_REPLIES = [
  'Hi! 👋 Thanks for reaching out — how can we help you today?',
  'Could you please share your registered mobile number?',
  'I’m looking into this and will get back to you within 5 minutes.',
  'Your deposit request has been approved ✅',
  'Your deposit is still pending review — we’ll notify you once approved.',
  'Could you share a screenshot of the issue?',
  'Thanks for your patience! Anything else we can help with?',
];

function QuickReplyMenu({ onPick }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [list, setList] = useState(DEFAULT_QUICK_REPLIES);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getQuickReplies().then((arr) => {
      if (Array.isArray(arr) && arr.length) setList(arr);
    }).catch(() => {});
  }, []);

  const persist = async (next) => {
    setSaving(true);
    try {
      const saved = await saveQuickReplies(next);
      setList(saved);
      toast.success('Quick replies saved');
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const addReply = async () => {
    const t = draft.trim();
    if (!t) return;
    await persist([...list, t]);
    setDraft('');
  };
  const removeReply = async (idx) => persist(list.filter((_, i) => i !== idx));

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="h-10 w-10 rounded-full bg-zinc-800 hover:bg-zinc-700 text-zinc-300 flex items-center justify-center shrink-0"
        data-testid="chat-quickreply-btn"
        title="Quick replies"
      >
        <Zap size={18} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => { setOpen(false); setEditing(false); }} />
          <div
            className="absolute bottom-12 left-0 z-50 w-80 max-h-96 overflow-y-auto rounded-lg bg-zinc-900 border border-zinc-800 shadow-xl p-2"
            data-testid="chat-quickreply-menu"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-1 pb-2 border-b border-zinc-800 mb-1">
              <span className="text-[11px] uppercase tracking-wider text-zinc-400 font-bold">Quick Replies</span>
              <button
                onClick={() => setEditing((e) => !e)}
                className="text-[11px] text-[color:var(--gold)] hover:underline"
                data-testid="chat-quickreply-edit-toggle"
              >
                {editing ? 'Done' : 'Manage'}
              </button>
            </div>
            {list.length === 0 && (
              <div className="text-zinc-500 text-xs p-2">
                No quick replies yet. Click <span className="text-[color:var(--gold)]">Manage</span> to add some.
              </div>
            )}
            {list.map((t, idx) => (
              <div key={idx} className="flex items-start gap-1">
                <button
                  onClick={() => { if (!editing) { onPick(t); setOpen(false); } }}
                  disabled={editing}
                  className="flex-1 text-left text-xs text-zinc-200 hover:bg-zinc-800 rounded p-2 disabled:opacity-80 disabled:cursor-default"
                  data-testid={`chat-quickreply-${idx}`}
                >
                  {t}
                </button>
                {editing && (
                  <button
                    onClick={() => removeReply(idx)}
                    className="shrink-0 m-1 p-1 rounded text-red-300 hover:bg-red-600/15"
                    data-testid={`chat-quickreply-del-${idx}`}
                    aria-label="Delete"
                  >
                    <span className="text-sm leading-none">×</span>
                  </button>
                )}
              </div>
            ))}
            {editing && (
              <div className="mt-2 pt-2 border-t border-zinc-800 flex items-stretch gap-1">
                <input
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addReply()}
                  placeholder="Type a new quick reply…"
                  className="flex-1 h-9 px-2 rounded bg-zinc-950 border border-zinc-800 text-xs text-zinc-100"
                  data-testid="chat-quickreply-new-input"
                />
                <button
                  onClick={addReply}
                  disabled={saving}
                  className="h-9 px-3 rounded bg-[color:var(--gold)] text-black text-xs font-bold disabled:opacity-60"
                  data-testid="chat-quickreply-add-btn"
                >
                  Add
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/* ──────────────────────────  Voice record  ───────────────────────────── */
function VoiceRecordButton({ onSend }) {
  const [recording, setRecording] = useState(false);
  const mediaRecRef = useRef(null);
  const chunksRef = useRef([]);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/ogg';
      const rec = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: mime });
        stream.getTracks().forEach((t) => t.stop());
        if (blob.size > 200) onSend(blob);
      };
      mediaRecRef.current = rec;
      rec.start();
      setRecording(true);
    } catch { toast.error('Microphone permission denied'); }
  };
  const stop = () => {
    mediaRecRef.current?.stop();
    setRecording(false);
  };

  return (
    <button
      onMouseDown={start}
      onMouseUp={stop}
      onMouseLeave={() => recording && stop()}
      onTouchStart={start}
      onTouchEnd={stop}
      className={`h-10 w-10 rounded-full flex items-center justify-center shrink-0 transition ${recording ? 'bg-red-500 text-white animate-pulse' : 'bg-zinc-800 hover:bg-zinc-700 text-zinc-300'}`}
      data-testid="chat-voice-btn"
      title="Press & hold to record voice"
      aria-label="Press and hold to record voice"
    >
      {recording ? <Square size={18} /> : <Mic size={18} />}
    </button>
  );
}
